import React, { useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
  Grid,
  InputLabel,
  TextField,
  Typography,
  Select,
  ListItemText,
  ListItemIcon,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Button,
} from '@mui/material';
import { Stop } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {
  GoogleMap, Polyline, Circle,
} from '@react-google-maps/api';

import MultiSelect from '../../common/multi-select';
import pickupZoneActions from '../../../actions/pickupZones';
import * as snackbarActions from '../../../actions/snackbar';
import {
  VehicleZonesStatusNames,
  VehicleZoneCategories,
  LinkAdminVehicleZoneCategories,
  ZoneHexColors,
  VehicleZoneTypes,
} from '../../../lib/utils';
import ZonePolygon from '../map/zone-polygon';
import CustomMapControl from '../vehicles-page/custom-map-control';

const { google } = window;

// Google removed the Drawing Library's DrawingManager from the Maps JavaScript
// API (as of v3.65), so boundary drawing is implemented manually below:
// clicking on the map while `isDrawing` is true appends a vertex, and clicking
// near the starting vertex closes/completes the polygon.
const CLOSE_POLYGON_DISTANCE_METERS = 25;

const styles = {
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  flexColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  labelBar: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: '18px',
    alignItem: 'flex-end',
  },
  mapDiv: {
    height: '800px',
    width: '800px',
    paddingTop: '10px',
  },
};

function BasicMap({
  zoom,
  center,
  handleNewPolygon,
  onZoneBoundaryEdit,
  zones,
  allowNewZoneCreation,
  showLabels,
  vehicleZoneId,
}) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygonPath, setPolygonPath] = useState([]);
  const polygonPathRef = useRef([]);

  const resetDrawingState = () => {
    setIsDrawing(false);
    polygonPathRef.current = [];
    setPolygonPath([]);
  };

  // Build a real array of google.maps.LatLng objects, matching the shape
  // `polygon.getPath().getArray()` used to hand to onPolygonComplete, since
  // downstream code (convertPolyToCoords) expects points with lat()/lng().
  const finishDrawing = (path) => {
    resetDrawingState();
    if (path.length < 3) return;
    const latLngPath = path.map((point) => new google.maps.LatLng(point.lat, point.lng));
    handleNewPolygon(latLngPath);
  };

  const onMapClick = (event) => {
    if (!isDrawing) return;
    const currentPath = polygonPathRef.current;
    if (currentPath.length >= 3) {
      const firstPoint = currentPath[0];
      const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(firstPoint.lat, firstPoint.lng),
        event.latLng,
      );
      if (distanceMeters <= CLOSE_POLYGON_DISTANCE_METERS) {
        finishDrawing(currentPath);
        return;
      }
    }
    const newPath = [
      ...currentPath,
      { lat: event.latLng.lat(), lng: event.latLng.lng() },
    ];
    polygonPathRef.current = newPath;
    setPolygonPath(newPath);
  };

  const onDrawButtonClick = () => {
    if (isDrawing) {
      resetDrawingState();
    } else {
      setIsDrawing(true);
    }
  };

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      zoom={zoom}
      center={center}
      onClick={onMapClick}
      options={{
        draggableCursor: isDrawing ? 'crosshair' : undefined,
      }}
    >
      {allowNewZoneCreation && (
        <CustomMapControl position={google.maps.ControlPosition.TOP_CENTER}>
          <Grid container direction="column" alignItems="center" sx={{ mt: 1.5 }}>
            <Button
              style={{
                backgroundColor: 'white',
                borderWidth: 1,
              }}
              onClick={onDrawButtonClick}
              variant="outlined"
              color="primary"
            >
              {isDrawing ? 'Cancel Drawing' : 'Draw Zone'}
              {isDrawing
                ? (<CloseIcon sx={{ ml: 1, color: 'grey.600' }} />)
                : (<AddIcon sx={{ ml: 1, color: 'grey.600' }} />)}
            </Button>
            {isDrawing && (
              <Grid
                sx={{
                  mt: 1, p: 1, bgcolor: 'white', borderRadius: 1, fontSize: 12,
                }}
              >
                Click on the map to draw a zone boundary.
                <br />
                Click the highlighted starting point to finish.
              </Grid>
            )}
          </Grid>
        </CustomMapControl>
      )}
      {isDrawing && polygonPath.length > 0 && (
        <Polyline
          path={polygonPath}
          options={{ strokeColor: '#4285F4', strokeWeight: 2, clickable: false }}
        />
      )}
      {isDrawing && polygonPath.length >= 3 && (
        <Circle
          center={polygonPath[0]}
          radius={CLOSE_POLYGON_DISTANCE_METERS}
          options={{
            strokeColor: '#4285F4',
            fillColor: '#4285F4',
            fillOpacity: 0.35,
            clickable: false,
          }}
        />
      )}
      {zones.map((zone) => (
        <ZonePolygon
          key={zone._id}
          zone={zone}
          editable={zone._id === vehicleZoneId}
          onZoneBoundaryEdit={onZoneBoundaryEdit}
          showLabels={showLabels || zone._id === vehicleZoneId}
        />
      ))}
    </GoogleMap>
  );
}

class VehicleZoneMap extends React.Component {
  static DEFAULT_ZOOM_LEVEL = 14;

  static DEFAULT_LAT = 0;

  static DEFAULT_LNG = 0;

  static DEFAULT_CENTER = { lat: VehicleZoneMap.DEFAULT_LAT, lng: VehicleZoneMap.DEFAULT_LNG };

  constructor(props) {
    super(props);
    const { selectedMarket } = props;

    const selectedMarketGeo = get(selectedMarket, 'geo.coordinates', [VehicleZoneMap.DEFAULT_LNG, VehicleZoneMap.DEFAULT_LAT]);
    const marketCenter = { lat: selectedMarketGeo[1], lng: selectedMarketGeo[0] };

    const center = marketCenter || VehicleZoneMap.DEFAULT_CENTER;
    const zoom = VehicleZoneMap.DEFAULT_ZOOM_LEVEL;

    this.state = {
      zoom,
      center,
      showLabels: false,
      filterCategory: [],
    };
  }

  get editableZones() {
    const { zones, vehicleZoneId } = this.props;
    return zones.filter((zone) => zone._id === vehicleZoneId && !get(zone, 'boundary.coordinates.length'));
  }

  handleToggleLabels = () => {
    this.setState(({ showLabels }) => ({ showLabels: !showLabels }));
  };

  handleCategoryFilter(value) {
    this.setState({ filterCategory: value });
  }

  onChangeNumberField = (event) => {
    const { name } = event.target;
    const value = Math.max(parseInt(event.target.value, 10), 0);

    this.props.onChange({ target: { name, value } });
    // eslint-disable-next-line no-param-reassign
    event.target.value = value;
  }

  render() {
    const {
      vehicleZones,
      pickupZones,
      vehicleZoneId,
      pickUpZonesLoading,
      onChange,
      onAutomationChange,
      onEditBoundary,
      onCreateBoundary,
      currentZone,
    } = this.props;
    const {
      zoom,
      center,
      showLabels,
      filterCategory,
    } = this.state;

    const pickupZone = pickupZones.find((zone) => zone._id === vehicleZoneId);
    const activeVehicleZones = vehicleZones.filter(
      (zone) => zone.status === VehicleZonesStatusNames.ACTIVE,
    );
    const filteredVehicleZones = activeVehicleZones.filter(
      (zone) => zone.status === VehicleZonesStatusNames.ACTIVE
        && filterCategory
        && filterCategory.includes(zone.category),
    );
    const zonesToDisplay = pickupZones.filter((zone) => zone._id === vehicleZoneId
    || (zone.status === VehicleZonesStatusNames.ACTIVE && filterCategory
      && filterCategory.includes(VehicleZoneCategories.PICKUP)
    )).concat(filteredVehicleZones);
    const hasCoordinates = !!get(pickupZone, 'boundary.coordinates.length');

    const availableZoneCategories = [...new Set(activeVehicleZones.map((zone) => zone.category))];
    availableZoneCategories.push(VehicleZoneCategories.PICKUP);

    const zoneCategoriesToDisplay = Object.keys(LinkAdminVehicleZoneCategories)
      .filter((c) => availableZoneCategories.includes(VehicleZoneCategories[c]));

    const currentAutomation = currentZone.automation || {};
    const equityEmailAutomationDisabled = !currentAutomation.createCaseWhenUnderMin
      && !currentAutomation.createCaseWhenOverMax;
    const highRiskEmailAutomationDisabled = !currentAutomation.createCaseForScooterInHighRiskZone;

    return (
      <Grid container style={styles.flexRow}>
        <Grid item xs={4} lg={3} style={styles.flexColumn}>
          <Grid container sx={{ width: 270, pr: 2.5 }}>
            <Grid item xs={12}>
              <Typography variant="h5">Zone Builder</Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid item>
                <Grid item sx={{ pt: 3 }}>
                  <InputLabel sx={{ fontSize: 'default' }}>
                    Name
                  </InputLabel>
                  <TextField
                    margin="dense"
                    name="name"
                    type="text"
                    value={currentZone.name || ''}
                    fullWidth
                    onChange={onChange}
                  />
                </Grid>
                <Grid item sx={{ pt: 2 }}>
                  <InputLabel sx={{ fontSize: 'default' }}>
                    Color
                  </InputLabel>
                  <Select
                    onChange={onChange}
                    value={currentZone.hexColor || ''}
                    name="hexColor"
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 0.5 }}
                  >
                    {Object.keys(ZoneHexColors).map((key) => (
                      <MenuItem
                        dense
                        key={ZoneHexColors[key]}
                        value={ZoneHexColors[key]}
                      >
                        <Grid container item alignItems="center">
                          <ListItemIcon>
                            <Stop style={{ color: ZoneHexColors[key] }} />
                          </ListItemIcon>
                          <ListItemText style={{ fontWeight: 'bold' }} primary={key} />
                        </Grid>
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item sx={{ pt: 2, display: 'none' }}>
                  <InputLabel sx={{ fontSize: 'default' }}>
                    Zone type
                  </InputLabel>
                  <Select
                    onChange={onChange}
                    value={currentZone.type || VehicleZoneTypes.PICKUP}
                    name="type"
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 0.5 }}
                  >
                    {Object.keys(VehicleZoneTypes).map((key) => (
                      <MenuItem
                        dense
                        key={VehicleZoneTypes[key]}
                        value={VehicleZoneTypes[key]}
                      >
                        <ListItemText primary={`${VehicleZoneTypes[key]} Zone`} />
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {currentZone.type !== VehicleZoneTypes.HIGH_RISK && (
                  <Grid item sx={{ pt: 2, display: 'none' }}>
                    <InputLabel sx={{ fontSize: 'default' }}>
                      Target deployment
                    </InputLabel>
                    <TextField
                      margin="dense"
                      name="target"
                      type="number"
                      variant="standard"
                      defaultValue={currentZone.target || ''}
                      fullWidth
                      onBlur={this.onChangeNumberField}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                )}

                {currentZone.type === VehicleZoneTypes.EQUITY && (
                  <Grid>
                    <Grid item sx={{ pt: 2 }}>
                      <InputLabel sx={{ fontSize: 'default' }}>
                        Minimum scooter count
                      </InputLabel>
                      <TextField
                        name="minimum"
                        type="number"
                        variant="standard"
                        defaultValue={currentZone.minimum || 0}
                        fullWidth
                        onBlur={this.onChangeNumberField}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item sx={{ pt: 2 }}>
                      <InputLabel sx={{ fontSize: 'default' }}>
                        Maximum scooter count
                      </InputLabel>
                      <TextField
                        name="maximum"
                        type="number"
                        variant="standard"
                        defaultValue={currentZone.maximum || ''}
                        fullWidth
                        onBlur={this.onChangeNumberField}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item sx={{ pt: 2 }}>
                      <InputLabel sx={{ fontSize: 'default' }}>
                        Create a case when
                      </InputLabel>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            name="createCaseWhenUnderMin"
                            checked={currentAutomation.createCaseWhenUnderMin || false}
                            onChange={onAutomationChange}
                          />
                        )}
                        label="Vehicle is under minimum"
                        sx={{ ml: 0.25, mt: 0 }}
                      />
                      <FormControlLabel
                        control={(
                          <Checkbox
                            name="createCaseWhenOverMax"
                            checked={currentAutomation.createCaseWhenOverMax || false}
                            onChange={onAutomationChange}
                          />
                        )}
                        label="Vehicle is over maximum"
                        sx={{ ml: 0.25, mt: 0 }}
                      />
                      <FormControlLabel
                        control={(
                          <Checkbox
                            name="sendEmailWhenCaseCreated"
                            checked={currentAutomation.sendEmailWhenCaseCreated || false}
                            disabled={equityEmailAutomationDisabled}
                            onChange={onAutomationChange}
                          />
                        )}
                        label="Send email upon case creation"
                        sx={{ ml: 0.25, mt: 0 }}
                      />
                    </Grid>
                  </Grid>
                )}

                {currentZone.type === VehicleZoneTypes.HIGH_RISK && (
                  <Grid>
                    <Grid item sx={{ pt: 2 }}>
                      <InputLabel sx={{ fontSize: 'default' }}>
                        Create a case when
                      </InputLabel>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            name="createCaseForScooterInHighRiskZone"
                            checked={currentAutomation.createCaseForScooterInHighRiskZone || false}
                            onChange={onAutomationChange}
                          />
                        )}
                        label="Vehicle ends a trip here"
                        sx={{ ml: 0.25, mt: 0 }}
                      />
                      <FormControlLabel
                        control={(
                          <Checkbox
                            name="sendEmailWhenCaseCreated"
                            checked={currentAutomation.sendEmailWhenCaseCreated || false}
                            disabled={highRiskEmailAutomationDisabled}
                            onChange={onAutomationChange}
                          />
                        )}
                        label="Send email upon case creation"
                        sx={{ ml: 0.25, mt: 0 }}
                      />
                    </Grid>
                    <Grid item sx={{ pt: 2 }}>
                      <InputLabel sx={{ fontSize: 'default' }}>
                        Create a Rebalance task when
                      </InputLabel>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            name="createTaskForScooterInHighRiskZone"
                            checked={currentAutomation.createTaskForScooterInHighRiskZone || false}
                            onChange={onAutomationChange}
                          />
                        )}
                        label="Any vehicle ends a trip here"
                        sx={{ ml: 0.25 }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={8} lg={9}>
          <Grid container style={styles.flexRow}>
            <Grid item xs={6}>
              <InputLabel sx={{ fontSize: 'default', mb: 1 }}>
                Map Layers
              </InputLabel>
              <MultiSelect
                value={filterCategory}
                options={zoneCategoriesToDisplay.map((c) => ({
                  value: VehicleZoneCategories[c],
                  label: LinkAdminVehicleZoneCategories[c],
                }))}
                handleChange={(value) => this.handleCategoryFilter(value)}
              />
            </Grid>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showLabels}
                  onChange={this.handleToggleLabels}
                />
              )}
              label="Labels"
              sx={{ ml: 5, mt: 3 }}
            />
          </Grid>

          <Grid item xs={12}>
            {!pickUpZonesLoading && (
              <div id="map" style={styles.mapDiv}>
                <BasicMap
                  containerElement={<div style={{ height: '100%' }} />}
                  mapElement={<div style={{ height: '100%' }} />}
                  zoom={zoom}
                  center={center}
                  zones={zonesToDisplay}
                  allowNewZoneCreation={!hasCoordinates}
                  handleNewPolygon={(polygon) => onCreateBoundary(polygon)}
                  onZoneBoundaryEdit={(polygon) => onEditBoundary(polygon)}
                  showLabels={showLabels}
                  vehicleZoneId={vehicleZoneId}
                />
              </div>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

VehicleZoneMap.defaultProps = {
  zones: [],
  pickUpZonesLoading: false,
  vehicleZones: [],
};
VehicleZoneMap.propTypes = {
  selectedMarket: PropTypes.shape({
    geo: PropTypes.shape({ coordinates: PropTypes.array }),
  }).isRequired,
  pickUpZonesLoading: PropTypes.bool,
  pickupZones: PropTypes.arrayOf(PropTypes.object).isRequired,
  vehicleZones: PropTypes.arrayOf(PropTypes.object),
  zones: PropTypes.arrayOf(PropTypes.object),
};

function mapStateToProps({ markets, pickupZones, vehicleZones }) {
  return {
    markets: markets.markets,
    selectedMarket: markets.selectedMarket,
    pickupZones: pickupZones.pickupZones,
    pickupZonesLoading: pickupZones.isLoading,
    vehicleZones: vehicleZones.vehicleZones,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    ...pickupZoneActions,
    ...snackbarActions,
  }, dispatch);
}

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(VehicleZoneMap));
